<template>
  <el-drawer :visible.sync="localVisible" :with-header="false" size="440px" :append-to-body="true" @open="handleOpen">
    <div class="rf">
      <div class="rf__top">
        <div class="rf__title">
          <i class="el-icon-bank-card"></i>
          <span>{{ $t("refund.title") }}</span>
        </div>
        <el-button type="text" icon="el-icon-close" @click="close"></el-button>
      </div>

      <div class="rf__body">
        <!-- 订单摘要 -->
        <div class="rf__summary">
          <div class="rf__sum-row">
            <span>{{ $t("order.equipmentName") }}</span>
            <strong>{{ order.equipmentName || order.equipmentNo || "-" }}</strong>
          </div>
          <div class="rf__sum-row">
            <span>{{ $t("order.orderNumber") }}</span>
            <strong class="rf__mono">{{ order.orderNumber || "-" }}</strong>
          </div>
          <div class="rf__sum-row">
            <span>{{ $t("order.actualPrice") }}</span>
            <strong>{{ money(order.payablePrice) }}</strong>
          </div>
          <div v-if="cardLabel" class="rf__sum-row">
            <span>{{ $t("refund.card") }}</span>
            <strong>{{ cardLabel }}</strong>
          </div>
          <div v-if="alreadyRefunded" class="rf__sum-row rf__sum-row--warn">
            <span>{{ $t("order.refundPrice") }}</span>
            <strong>{{ money(order.refundPrice) }}</strong>
          </div>
        </div>

        <el-alert
          v-if="remaining <= 0"
          :title="$t('refund.fullyRefunded')"
          type="info"
          :closable="false"
          show-icon
          class="rf__alert"
        />

        <template v-else>
          <el-form label-position="top" class="rf__form">
            <el-form-item :label="$t('refund.amount')">
              <el-input-number
                v-model="amount"
                :min="0.01"
                :max="remaining"
                :precision="2"
                :step="0.5"
                controls-position="right"
                class="rf__num"
              ></el-input-number>
              <div class="rf__hint">
                {{ $t("refund.maxHint", { max: money(remaining) }) }}
                <el-button type="text" size="mini" @click="amount = remaining">{{ $t("refund.full") }}</el-button>
              </div>
            </el-form-item>
            <el-form-item :label="$t('refund.reason')">
              <el-select v-model="reason" size="small" style="width: 100%" :placeholder="$t('refund.reasonPlaceholder')">
                <el-option v-for="r in reasons" :key="r.value" :label="r.label" :value="r.value"></el-option>
              </el-select>
            </el-form-item>
            <el-form-item :label="$t('refund.note')">
              <el-input v-model="note" type="textarea" :rows="2" :placeholder="$t('refund.notePlaceholder')"></el-input>
            </el-form-item>
          </el-form>
        </template>

        <!-- 退款记录 -->
        <div class="rf__section-head">
          <span>{{ $t("refund.history") }}</span>
          <el-tag size="mini" type="info">{{ records.length }}</el-tag>
        </div>
        <div v-loading="recordsLoading" class="rf__records">
          <div v-if="records.length" class="rf__rec-list">
            <div v-for="(rec, i) in records" :key="rec.id || i" class="rf__rec">
              <div class="rf__rec-top">
                <span :class="['vtm-pill', recStateClass(rec.rs)]">{{ recStateLabel(rec.rs) }}</span>
                <strong class="vtm-num">{{ money(rec.price) }}</strong>
              </div>
              <div class="rf__rec-sub">
                <span>{{ rec.createTime }}</span>
                <span v-if="rec.resultInfo" class="rf__rec-msg" :title="rec.resultInfo">{{ rec.resultInfo }}</span>
              </div>
            </div>
          </div>
          <div v-else-if="!recordsLoading" class="rf__empty">{{ $t("refund.noHistory") }}</div>
        </div>
      </div>

      <div class="rf__footer">
        <el-button size="small" @click="close">{{ $t("common.cancel") }}</el-button>
        <el-button size="small" type="danger" :loading="submitting" :disabled="remaining <= 0 || !amount" @click="submit">{{
          $t("refund.issue")
        }}</el-button>
      </div>
    </div>
  </el-drawer>
</template>

<script>
  // 统一退款抽屉：金额（默认剩余可退，可部分）+ 原因 + 备注，并展示该订单的退款记录。
  // 沿用现有端点 order/refund（POST {orderId, ...}）——附带 amount/reason/refundInfo，
  // 后端支持部分退款/原因时即生效；仅支持整单时全额退款仍可用。退款记录来自 ordersRefund/page(orderNumber)。
  import { orderCardLabel } from "@/utils/paymentInfo";

  export default {
    name: "RefundDialog",
    props: {
      visible: { type: Boolean, default: false },
      order: { type: Object, default: () => ({}) }
    },
    data() {
      return {
        amount: 0,
        reason: "",
        note: "",
        submitting: false,
        records: [],
        recordsLoading: false
      };
    },
    computed: {
      localVisible: {
        get() {
          return this.visible;
        },
        set(v) {
          if (!v) {
            this.close();
          }
        }
      },
      cardLabel() {
        return orderCardLabel(this.order);
      },
      alreadyRefunded() {
        return Number(this.order.refundPrice) > 0;
      },
      remaining() {
        const charged = Number(this.order.payablePrice) || 0;
        const refunded = Number(this.order.refundPrice) || 0;
        return Math.max(0, Math.round((charged - refunded) * 100) / 100);
      },
      reasons() {
        return ["notDispensed", "misCharge", "quality", "duplicate", "goodwill", "other"].map(k => ({
          value: k,
          label: this.$t("refund.reason_" + k)
        }));
      }
    },
    methods: {
      money(v) {
        if (v === null || v === undefined || v === "") {
          return "$0.00";
        }
        const n = Number(v);
        return Number.isNaN(n) ? String(v) : `$${n.toFixed(2)}`;
      },
      handleOpen() {
        this.amount = this.remaining;
        this.reason = "";
        this.note = "";
        this.loadRecords();
      },
      close() {
        this.$emit("close");
      },
      loadRecords() {
        if (!this.order.orderNumber) {
          this.records = [];
          return;
        }
        this.recordsLoading = true;
        this.$api
          .request(this.$url.RefundRecord.page, this.$method.get, { orderNumber: this.order.orderNumber, pageIndex: 1, pageSize: 20 })
          .then(res => {
            this.recordsLoading = false;
            if (res.code === 0 && res.data) {
              this.records = res.data.records || [];
            }
          })
          .catch(() => {
            this.recordsLoading = false;
          });
      },
      recStateClass(rs) {
        return Number(rs) === 1 ? "vtm-pill--ok" : Number(rs) === -1 ? "vtm-pill--crit" : "vtm-pill--info";
      },
      recStateLabel(rs) {
        return Number(rs) === 1
          ? this.$t("orderRefund.success")
          : Number(rs) === -1
          ? this.$t("orderRefund.error")
          : this.$t("orderRefund.refunding");
      },
      submit() {
        const reasonLabel = this.reason ? this.$t("refund.reason_" + this.reason) : "";
        const refundInfo = [reasonLabel, this.note].filter(Boolean).join(" · ");
        this.$confirm(this.$t("refund.confirm", { amount: this.money(this.amount) }), this.$t("common.tip"), {
          confirmButtonText: this.$t("refund.issue"),
          cancelButtonText: this.$t("common.cancel"),
          type: "warning"
        })
          .then(() => {
            this.submitting = true;
            return this.$api.request(this.$url.Order.refundByOrderId, this.$method.post, {
              orderId: this.order.id,
              amount: Number(this.amount),
              refundPrice: Number(this.amount),
              reason: this.reason,
              refundInfo
            });
          })
          .then(res => {
            this.submitting = false;
            if (!res) {
              return;
            }
            if (res.code === 0) {
              this.$message.success(res.msg || this.$t("refund.issued"));
              this.$emit("refresh");
              this.close();
            } else {
              this.$message.error(res.msg || this.$t("common.loadFailed"));
            }
          })
          .catch(err => {
            this.submitting = false;
            if (err && err.msg) {
              this.$message.error(err.msg);
            }
          });
      }
    }
  };
</script>

<style lang="scss" scoped>
  .rf {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: var(--vtm-bg);
  }
  .rf__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    background: var(--vtm-surface);
    border-bottom: 1px solid var(--vtm-line);
  }
  .rf__title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: var(--vtm-display);
    font-size: 16px;
    font-weight: 600;
    color: var(--vtm-heading);

    i {
      color: var(--vtm-crit);
    }
  }
  .rf__body {
    flex: 1;
    overflow-y: auto;
    padding: 16px 18px;
  }
  .rf__summary {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 14px 16px;
    border: 1px solid var(--vtm-line);
    border-radius: var(--vtm-r-md);
    background: var(--vtm-surface-2);
    margin-bottom: 16px;
  }
  .rf__sum-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    font-size: 13px;

    span {
      color: var(--vtm-ink-3);
    }
    strong {
      color: var(--vtm-ink);
    }
  }
  .rf__sum-row--warn strong {
    color: var(--vtm-warn);
  }
  .rf__mono {
    font-family: var(--vtm-mono);
    font-weight: 500;
  }
  .rf__alert {
    margin-bottom: 12px;
  }
  .rf__form ::v-deep .el-form-item {
    margin-bottom: 12px;
  }
  .rf__num {
    width: 160px;
  }
  .rf__hint {
    font-size: 11.5px;
    color: var(--vtm-ink-3);
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .rf__section-head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 18px 0 10px;
    font-family: var(--vtm-display);
    font-size: 13px;
    font-weight: 600;
    color: var(--vtm-heading);
  }
  .rf__rec-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .rf__rec {
    padding: 10px 12px;
    border: 1px solid var(--vtm-line);
    border-radius: var(--vtm-r-sm);
    background: var(--vtm-surface);
  }
  .rf__rec-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
  .rf__rec-sub {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: 4px;
    font-size: 11px;
    color: var(--vtm-ink-3);
  }
  .rf__rec-msg {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 55%;
  }
  .rf__empty {
    font-size: 12.5px;
    color: var(--vtm-ink-3);
    text-align: center;
    padding: 14px 0;
  }
  .rf__footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 18px;
    background: var(--vtm-surface);
    border-top: 1px solid var(--vtm-line);
  }
</style>
